import { useEffect, useState } from "react";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { X, Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_SIZES = ["XS", "S", "M", "L", "XL", "XXL"];

export default function ProductForm({ product, categories, onClose, onSaved }) {
  const isEdit = !!product;
  const [form, setForm] = useState(() => ({
    name: product?.name || "",
    sku: product?.sku || "",
    category_id: product?.category_id || "",
    description: product?.description || "",
    color: product?.color || "",
    price: product?.price ?? 0,
    cost_price: product?.cost_price ?? 0,
    low_stock_threshold: product?.low_stock_threshold ?? 5,
    sizes: product?.sizes?.length ? product.sizes : DEFAULT_SIZES.map((s) => ({ size: s, stock: 0, low_stock_threshold: null })),
    images: product?.images || [],
  }));
  const [busy, setBusy] = useState(false);
  const [productId, setProductId] = useState(product?.id || null);

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }
  function setSize(idx, k, v) {
    const sizes = [...form.sizes];
    sizes[idx] = { ...sizes[idx], [k]: k === "stock" ? Number(v) || 0 : v };
    set("sizes", sizes);
  }

  async function save() {
    if (!form.name || !form.sku) { toast.error("Name and SKU are required"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price) || 0,
        cost_price: Number(form.cost_price) || 0,
        low_stock_threshold: Number(form.low_stock_threshold) || 0,
        sizes: form.sizes.filter((s) => s.size).map((s) => ({
          size: s.size,
          stock: Number(s.stock) || 0,
          low_stock_threshold: s.low_stock_threshold === null || s.low_stock_threshold === undefined || s.low_stock_threshold === "" ? null : Number(s.low_stock_threshold),
        })),
      };
      let res;
      if (isEdit) res = await api.put(`/products/${product.id}`, payload);
      else res = await api.post("/products", payload);
      setProductId(res.data.id);
      toast.success(isEdit ? "Product updated" : "Product created");
      if (!isEdit) {
        // keep modal open so user can upload images
        setForm((f) => ({ ...f, ...res.data }));
      } else {
        onSaved();
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  }

  async function uploadImage(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!productId) { toast.error("Save the product first to upload images."); return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/products/${productId}/upload-image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, images: [...(f.images || []), data.path] }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      e.target.value = "";
    }
  }

  async function removeImage(path) {
    if (!productId) return;
    try {
      await api.delete(`/products/${productId}/image`, { params: { path } });
      setForm((f) => ({ ...f, images: (f.images || []).filter((x) => x !== path) }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center overflow-y-auto py-8 px-4" data-testid="product-form-modal">
      <div className="brut-card w-full max-w-3xl bg-white">
        <div className="flex items-center justify-between p-5 border-b-2 border-black">
          <div>
            <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">{isEdit ? "EDIT" : "NEW"}</div>
            <h2 className="font-display font-black text-2xl tracking-tight">{isEdit ? "Edit Product" : "New Product"}</h2>
          </div>
          <button onClick={onClose} data-testid="close-product-form" className="p-2 hover:bg-zinc-100"><X size={20} /></button>
        </div>

        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Name *">
              <input className="brut-input" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="product-name-input" />
            </Field>
            <Field label="SKU *">
              <input className="brut-input font-mono-ds" value={form.sku} onChange={(e) => set("sku", e.target.value)} data-testid="product-sku-input" />
            </Field>
            <Field label="Category">
              <select className="brut-input" value={form.category_id} onChange={(e) => set("category_id", e.target.value)} data-testid="product-category-select">
                <option value="">— None —</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Color">
              <input className="brut-input" value={form.color} onChange={(e) => set("color", e.target.value)} data-testid="product-color-input" />
            </Field>
            <Field label="Price (₹)">
              <input type="number" className="brut-input font-mono-ds" value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="product-price-input" />
            </Field>
            <Field label="Cost Price (₹)">
              <input type="number" className="brut-input font-mono-ds" value={form.cost_price} onChange={(e) => set("cost_price", e.target.value)} data-testid="product-cost-input" />
            </Field>
            <Field label="Default Low-Stock Threshold (fallback for sizes without a per-size value)">
              <input type="number" className="brut-input font-mono-ds" value={form.low_stock_threshold} onChange={(e) => set("low_stock_threshold", e.target.value)} data-testid="product-threshold-input" />
            </Field>
          </div>

          <Field label="Description">
            <textarea rows={3} className="brut-input" value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="product-description-input" />
          </Field>

          <div>
            <div className="font-mono-ds text-[11px] tracking-widest uppercase mb-1">Size Variants · Stock · Per-Size Threshold</div>
            <div className="font-mono-ds text-[10px] text-zinc-500 mb-3">Leave threshold blank to use the default above. Set a value to override per size.</div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
              {form.sizes.map((s, i) => (
                <div key={i} className="brut-card-flat p-2">
                  <input className="brut-input text-center font-display font-bold mb-1" value={s.size} onChange={(e) => setSize(i, "size", e.target.value)} data-testid={`size-input-${i}`} placeholder="Size" />
                  <input type="number" className="brut-input text-center font-mono-ds mb-1" value={s.stock} onChange={(e) => setSize(i, "stock", e.target.value)} data-testid={`stock-input-${i}`} placeholder="Stock" title="Stock" />
                  <input type="number" className="brut-input text-center font-mono-ds text-xs" value={s.low_stock_threshold ?? ""} onChange={(e) => setSize(i, "low_stock_threshold", e.target.value)} data-testid={`threshold-input-${i}`} placeholder="Thr" title={`Per-size threshold (default ${form.low_stock_threshold})`} />
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="font-mono-ds text-[11px] tracking-widest uppercase mb-3">Images</div>
            {!productId && <div className="brut-card-flat p-3 mb-3 bg-[#FFB800] font-mono-ds text-xs">SAVE PRODUCT FIRST TO UPLOAD IMAGES</div>}
            <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
              {(form.images || []).map((p) => (
                <div key={p} className="relative aspect-square brut-card-flat overflow-hidden">
                  <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => removeImage(p)} className="absolute top-1 right-1 bg-[#FF3333] text-white p-1" data-testid="remove-image-btn"><Trash2 size={12} /></button>
                </div>
              ))}
              {productId && (
                <label className="aspect-square brut-card-flat flex flex-col items-center justify-center cursor-pointer hover:bg-zinc-100" data-testid="upload-image-label">
                  <Upload size={20} />
                  <span className="font-mono-ds text-[10px] uppercase tracking-widest mt-2">Upload</span>
                  <input type="file" accept="image/*" className="hidden" onChange={uploadImage} data-testid="upload-image-input" />
                </label>
              )}
            </div>
          </div>
        </div>

        <div className="border-t-2 border-black p-5 flex justify-end gap-3">
          <button onClick={onClose} className="brut-btn-outline px-5 py-2.5" data-testid="cancel-product-btn">Cancel</button>
          <button onClick={save} disabled={busy} className="brut-btn px-5 py-2.5 disabled:opacity-60" data-testid="save-product-btn">
            {busy ? "Saving…" : (isEdit ? "Save Changes" : (productId ? "Done" : "Save Product"))}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="font-mono-ds text-[11px] tracking-widest uppercase mb-1.5">{label}</div>
      {children}
    </label>
  );
}
